import { useState, useRef } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function CaseStudySection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const items = content.items || [];
    const variant = config.variant || 'cards';
    const columns = config.columns || 2;
    const [carouselIndex, setCarouselIndex] = useState(0);
    const scrollRef = useRef(null);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const gridCls = columns === 1 ? 'md:grid-cols-1 max-w-3xl mx-auto' : columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2';

    const getItemLogo = (item) => {
        if (item.logo_folder_id && item.logo_file_id && mediaFolders[item.logo_folder_id]) {
            const found = mediaFolders[item.logo_folder_id].find(f => String(f.id) === String(item.logo_file_id));
            if (found) return found.image_url;
        }
        return item.client_logo || null;
    };

    // ── Card renderer ──
    const renderCard = (item, i, isFeatured = false) => {
        const logo = getItemLogo(item);
        return (
            <div key={i}
                className={`${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${isFeatured ? 'md:col-span-full' : ''}`}
                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                <div className={`p-6 ${isFeatured ? 'md:p-8' : ''}`}>
                    {/* Header: logo + client info */}
                    <div className="flex items-center gap-4 mb-5">
                        {logo ? (
                            <img src={logo} alt={item.client_name || ''} className="w-12 h-12 rounded-lg object-contain" loading="lazy"
                                style={{ backgroundColor: hexToRgba(s.accent, 0.04) }} />
                        ) : item.client_name ? (
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                                style={{ backgroundColor: s.accent }}>
                                {item.client_name.charAt(0).toUpperCase()}
                            </div>
                        ) : null}
                        <div>
                            {item.client_name && <h4 className="font-bold text-base" style={{ color: s.text }}>{item.client_name}</h4>}
                            {item.industry && (
                                <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full mt-0.5"
                                    style={{ backgroundColor: hexToRgba(s.secondary, 0.12), color: s.secondary }}>
                                    {item.industry}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Challenge */}
                    {item.challenge && (
                        <div className="mb-4">
                            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: s.accent }}>Desafio</span>
                            <p className={`text-sm mt-1 leading-relaxed ${isFeatured ? '' : 'line-clamp-3'}`} style={{ color: s.textSub }}>
                                {item.challenge}
                            </p>
                        </div>
                    )}

                    {/* Solution (featured only) */}
                    {isFeatured && item.solution && (
                        <div className="mb-4">
                            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: s.accent }}>Solucion</span>
                            <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.solution}</p>
                        </div>
                    )}

                    {/* Result metric */}
                    {item.result_metric && (
                        <div className="py-4 my-4 border-y" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                            <span className={`font-bold block ${isFeatured ? 'text-4xl' : 'text-3xl'}`} style={{ color: s.accent }}>
                                {item.result_metric}
                            </span>
                            {item.result_label && (
                                <span className="text-sm mt-1 block" style={{ color: s.textSub }}>{item.result_label}</span>
                            )}
                        </div>
                    )}

                    {/* Quote */}
                    {item.quote && (
                        <div className="mt-4">
                            <svg className="w-6 h-6 mb-2 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11h4v10H0z" />
                            </svg>
                            <p className="italic text-sm leading-relaxed" style={{ color: s.textSub }}>
                                &ldquo;{item.quote}&rdquo;
                            </p>
                            {item.quote_author && (
                                <p className="text-xs font-medium mt-2" style={{ color: s.text }}>
                                    &mdash; {item.quote_author}
                                </p>
                            )}
                        </div>
                    )}

                    {/* CTA */}
                    {item.cta_text && item.cta_link && (
                        <a {...resolveLinkProps(item.cta_link)}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold mt-5 hover:underline transition-colors"
                            style={{ color: s.accent }}>
                            {item.cta_text}
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                    )}
                </div>
            </div>
        );
    };

    // ── Featured variant ──
    if (variant === 'featured') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-6">
                        {/* First item: featured (full width) */}
                        {renderCard(items[0], 0, true)}
                        {/* Remaining items: grid */}
                        {items.length > 1 && (
                            <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                                {items.slice(1).map((item, i) => renderCard(item, i + 1))}
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Carousel variant ──
    if (variant === 'carousel') {
        const scrollLeft = () => {
            if (scrollRef.current) {
                scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
            }
        };
        const scrollRight = () => {
            if (scrollRef.current) {
                scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
            }
        };

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    <div className="relative">
                        {/* Arrows */}
                        {items.length > 1 && (
                            <>
                                <button onClick={scrollLeft}
                                    className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 hidden md:flex"
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }}>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button onClick={scrollRight}
                                    className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 hidden md:flex"
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }}>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Scrollable container */}
                        <div ref={scrollRef}
                            className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {items.map((item, i) => (
                                <div key={i} className="min-w-[340px] max-w-[400px] flex-shrink-0 snap-start">
                                    {renderCard(item, i)}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                    {items.map((item, i) => renderCard(item, i))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
